import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRICING } from "@/data/categories";
import { BookOpen, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";

const PRODUCT_LABELS = {
  lifetime_access: "Lifetime Access",
  memory_insight: "Memory Insight",
  yearly_reflection: "Yearly Reflection",
  lifetime_reflection: "Whole Life Reflection",
  life_story_ebook: "My Life Story Ebook",
  gift_lifetime: "Lifetime Access Gift",
};

const MAX_POLLS = 12;

export function PaymentResult({ mode, provider = "stripe" }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const isPaymongo = provider === "paymongo";
  const sessionId = isPaymongo ? params.get("order_id") : params.get("session_id");
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "polling");
  const [lookup, setLookup] = useState(null);
  const [giftCode, setGiftCode] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [year, setYear] = useState(null);
  const [orderNo, setOrderNo] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (mode === "cancel" || !sessionId) {
      if (mode !== "cancel") setState("error");
      return;
    }
    let timer;
    const statusUrl = isPaymongo
      ? `/payments/paymongo/status/${sessionId}`
      : `/payments/status/${sessionId}`;
    const check = async () => {
      try {
        const r = await api.get(statusUrl);
        setLookup(r.data.lookup_key);
        setEventId(r.data.event_id);
        setYear(r.data.year);
        setOrderNo(r.data.order_number);
        setGiftCode(r.data.gift_code);
        if (["refunded_duplicate", "refund_failed"].includes(r.data.payment_status)) {
          setState("duplicate");
          return;
        }
        if (r.data.payment_status === "paid") {
          await refreshUser();
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(check, 2000);
    };
    check();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, mode]);

  const isLifetime = lookup === "lifetime_access";
  const isInsight = lookup === "memory_insight";
  const label = (PRODUCT_LABELS[lookup] || "your purchase") + (year ? ` for ${year}` : "");

  return (
    <div className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-8 flex items-center gap-2">
        <BookOpen size={26} weight="duotone" className="text-[#B08D57]" />
        <span className="font-serif text-xl text-[#2C2A28]">Your Life Journeys Matter</span>
      </div>

      {state === "polling" && (
        <div data-testid="payment-polling">
          <CircleNotch size={44} className="mx-auto animate-spin text-[#B08D57]" />
          <h1 className="mt-5 font-serif text-3xl text-[#2C2A28]">Confirming your payment…</h1>
          <p className="mt-2 text-[#8A7F76]">This only takes a moment. Please don't close this page.</p>
        </div>
      )}

      {state === "paid" && lookup === "gift_lifetime" && (
        <div data-testid="payment-gift-ready">
          <CheckCircle size={52} weight="duotone" className="mx-auto text-[#7EA193]" />
          <p className="mt-4 font-handwriting text-3xl text-[#B08D57]">thank you</p>
          <h1 className="mt-1 font-serif text-4xl leading-tight text-[#2C2A28]">Your gift is ready</h1>
          <p className="mt-4 text-[#4A4540]">Here is the gift code. We've also emailed it to you with a link to the printable certificate.</p>
          {giftCode && <p data-testid="payment-gift-code" className="mx-auto mt-4 inline-block rounded-lg border border-[#D9A036] bg-white px-5 py-2 font-mono text-xl tracking-wider text-[#2D2824]">{giftCode}</p>}
          {orderNo && <p className="mt-2 text-xs text-[#8A7F76]">Order number: {orderNo}</p>}
          <Button data-testid="payment-gift-continue" onClick={() => navigate(`/gift?code=${giftCode || ""}`)}
            className="mt-7 rounded-full bg-[#E06D53] px-8 py-6 text-base text-white hover:bg-[#C85B43]">
            Download the gift certificate
          </Button>
        </div>
      )}

      {state === "paid" && lookup !== "gift_lifetime" && (
        <div data-testid="payment-success">
          <CheckCircle size={52} weight="duotone" className="mx-auto text-[#7EA193]" />
          <p className="mt-4 font-handwriting text-3xl text-[#B08D57]">thank you</p>
          <h1 className="mt-1 font-serif text-4xl leading-tight text-[#2C2A28]">
            {isLifetime ? "Every season is now yours" : `${label} is unlocked`}
          </h1>
          <p className="mt-4 text-[#4A4540]">
            {isLifetime
              ? "Your Lifetime Access is active. All of your life seasons are open, and your story is yours to keep — forever."
              : isInsight
                ? "Your payment is confirmed. Open your memory to create your Memory Insight — you can reread and download it anytime."
                : `Your purchase of ${label} is confirmed. Head to your Keepsakes to create it from your memories.`}
          </p>
          {orderNo && <p data-testid="payment-order-number" className="mt-2 text-xs text-[#8A7F76]">Order number: {orderNo}</p>}
          <Button
            data-testid="payment-success-continue"
            onClick={() => navigate(isLifetime ? "/timeline?celebrate=1" : isInsight ? `/timeline?memory=${eventId}&insight=1` : lookup === "life_story_ebook" ? "/ebook-studio" : year ? `/timeline?view=keepsakes&year=${year}` : "/timeline?view=keepsakes")}
            className="mt-7 rounded-full bg-[#B08D57] px-8 py-6 text-base text-[#FDFBF7] hover:-translate-y-0.5 hover:bg-[#C19A6B]"
          >
            {isLifetime ? "Continue to my journey" : isInsight ? "Open my memory" : lookup === "life_story_ebook" ? "Open my Ebook studio" : "Go to my Keepsakes"}
          </Button>
        </div>
      )}

      {state === "duplicate" && (
        <div data-testid="payment-duplicate">
          <CheckCircle size={52} weight="duotone" className="mx-auto text-[#7EA193]" />
          <h1 className="mt-4 font-serif text-3xl text-[#2C2A28]">You already owned {label}</h1>
          <p className="mt-2 text-[#4A4540]">
            This looks like a duplicate payment, so nothing extra was unlocked and it is being refunded to your
            original payment method. Processing time depends on your bank.
          </p>
          {orderNo && <p className="mt-2 text-xs text-[#8A7F76]">Order number: {orderNo} · questions: support@yourlifejourneysmatter.com</p>}
          <Button onClick={() => navigate("/timeline")} className="mt-7 rounded-full bg-[#B08D57] px-8 py-6 text-base text-[#FDFBF7] hover:bg-[#C19A6B]">
            Go to my journey
          </Button>
        </div>
      )}

      {state === "cancelled" && (
        <div data-testid="payment-cancelled">
          <XCircle size={52} weight="duotone" className="mx-auto text-[#C99A5B]" />
          <h1 className="mt-4 font-serif text-3xl text-[#2C2A28]">Payment cancelled</h1>
          <p className="mt-2 text-[#8A7F76]">
            No charge was made. Your memories are safe — you can complete your
            purchase whenever you're ready.
          </p>
          <Button
            data-testid="payment-cancel-back"
            onClick={() => navigate("/upgrade")}
            className="mt-7 rounded-full bg-[#B08D57] px-8 py-6 text-base text-[#FDFBF7] hover:bg-[#C19A6B]"
          >
            Back to Lifetime Access
          </Button>
        </div>
      )}

      {["failed", "timeout", "error"].includes(state) && (
        <div data-testid="payment-problem">
          <XCircle size={52} weight="duotone" className="mx-auto text-[#B08D57]" />
          <h1 className="mt-4 font-serif text-3xl text-[#2C2A28]">
            {state === "timeout" ? "Still processing…" : "We couldn't confirm your payment"}
          </h1>
          <p className="mt-2 text-[#8A7F76]">
            {state === "timeout"
              ? "Your payment may still be going through. If you were charged, your access will unlock shortly — please check back in a minute."
              : "If you weren't charged, you can try again. If you believe this is an error, please contact us."}
          </p>
          <div className="mt-7 flex justify-center gap-3">
            <Button
              onClick={() => navigate("/upgrade")}
              variant="outline"
              className="rounded-full border-[#4A4540]/25 px-6 py-6 text-[#2C2A28] hover:bg-[#F4EFE6]"
            >
              Back to options
            </Button>
            <Button
              onClick={() => navigate("/timeline")}
              className="rounded-full bg-[#B08D57] px-6 py-6 text-[#FDFBF7] hover:bg-[#C19A6B]"
            >
              Go to my journey
            </Button>
          </div>
          <p className="mt-3 text-xs text-[#8A7F76]">
            Prices shown in {PRICING.currency} (AUD).
          </p>
        </div>
      )}
    </div>
  );
}
